import React, { useState } from 'react';
import PropTypes from 'prop-types';
import axios from 'axios';
import { Button } from '@pattern-library/buttons';
import { RadioGroup } from '@pattern-library/radio';
import Banner from '@pattern-library/banner';
import { MagicWandIcon } from '@pattern-library/icon';
import { Error } from '@pattern-library/result';
import { sendAnalyticsEvent } from '../../utils/analytics';

const propTypes = {
    options: PropTypes.array.isRequired,
    requestId: PropTypes.string.isRequired,
    url: PropTypes.string.isRequired,
    isPPTInDefaultInvestment: PropTypes.bool.isRequired,
    isPlanGlidepathEnabled: PropTypes.bool.isRequired,
    isPPTGlidepathEnrolled: PropTypes.bool.isRequired,
    isPPTManagedAccountEnrolled: PropTypes.bool.isRequired,
    isPPTManagedAccountPending: PropTypes.bool.isRequired,
    investmentName: PropTypes.string.isRequired,
    managedAccountPlatformId: PropTypes.number,
    success: PropTypes.bool
};

function ExploreMyOptionsModal({
    options,
    requestId,
    url,
    isPPTInDefaultInvestment,
    isPlanGlidepathEnabled,
    isPPTGlidepathEnrolled,
    isPPTManagedAccountEnrolled,
    isPPTManagedAccountPending,
    investmentName,
    managedAccountPlatformId,
    success
}) {
    const dataError = !success || options.length === 0;
    const [samlResponse, setSamlResponse] = useState(null);
    const [arkPostUrl, setArkPostUrl] = useState(null);
    const [requesting, setRequesting] = useState(false);

    let [selectedOption, setSelectedOption] = useState(null);
    let [isError, setIsError] = useState(false);
    let [errorText, setErrorText] = useState('Make a selection to proceed.');
    let manageAdviceContent = [];

    const radioOptions = options.map(({ Name: label, Description: description, OptionId: id, AnalyticsEventName: value}) => 
        ({
            id: id.toString(),
            label: <div><p className="asc-mb-1" style={{fontWeight: '700', fontSize: '16px'}}>{label}</p><p className="asc-mb-0">{description}</p></div>,
            value: value
        }));

    const submitForm = () => {
        if (isPPTManagedAccountEnrolled || isPPTManagedAccountPending || selectedOption) {
            setIsError(false);
            sendData(selectedOption);
            sendAnalyticsEvent(options.find(option => option.OptionId === selectedOption).AnalyticsEventName);
            return;
        }

        setIsError(true);
        return;
    };

    const sendData = id => {
        setRequesting(true);

        const postBody = (isPPTManagedAccountEnrolled && managedAccountPlatformId) ? {
            maPlatformId: managedAccountPlatformId
        } : {
            optionId: id
        }

        axios
            .post(`${url}/${requestId}`, postBody)
            .then(response => {
                if (response.data?.hasNextStepUrl && response.data?.action) {
                    window.location = response.data?.action;
                } else if (response.data?.SamlResponse && response.data?.PostURL) {
                    setSamlResponse(response.data.SamlResponse);
                    setArkPostUrl(response.data.PostURL);
                    setTimeout(() => document.ssoForm.submit(), 100);
                } else {
                    setRequesting(false);
                    setIsError(true);
                    if (response.data?.Error === 'proxiedAdmin') {
                        setErrorText('This participant function is not available via proxy.');
                    } else {
                        setErrorText('An error occurred. Please try again later.');
                    }
                }
            })
            .catch(() => {
                setRequesting(false);
                setIsError(true);
                setErrorText('An error occurred. Please try again later.');
            });
    };

    const handleRadioChange = e => {
        const optionId = e.target.id;
        setIsError(false);
        setSelectedOption(optionId);
    };

    const closeModal = () => {
        $("#requestworkflow-popup-window").data("kendoWindow").close();
    }

    // Display a specific banner if one the following scenarios is true
    const getBanner = () => {
        if (isPPTInDefaultInvestment && isPlanGlidepathEnabled && isPPTGlidepathEnrolled) {
            // Scenario 1 - banner for an enrolled participant
            return (
                <Banner
                    id="banner1"
                    customAttrs={{ 'aria-live': 'assertive' }}
                    customIcon={<MagicWandIcon color="dark-gray" />}
                    hasCloseButton={false}
                    messageRichText={
                        <>
                            <p className="asc-mb-2">
                                You are currently using your company's investment strategy
                                appropriate for your age: 100% {investmentName}
                            </p>
                            <p className="asc-mb-2">
                                Your investments will be automatically adjusted over time to keep
                                you appropriately invested from now until retirement.
                            </p>
                            <p>
                                <strong>
                                    If you make a change to your investments, automatic adjustments
                                    will be stopped.
                                </strong>
                            </p>
                        </>
                    }
                />
            );
        } else if (isPPTInDefaultInvestment && !isPlanGlidepathEnabled) {
            // Scenario 2 - banner for a participant in default investments
            return (
                <Banner
                    id="banner2"
                    customAttrs={{ 'aria-live': 'assertive' }}
                    hasCloseButton={false}
                    hasIcon={false}
                    messageRichText={
                        <p>
                            You are currently using your company's default investment: 100%{' '}
                            {investmentName}
                        </p>
                    }
                />
            );
        } else {
            return;
        }
    };

    if (isPPTManagedAccountPending && !dataError) {
        return (
            <div className="asc-mb-4 asc-mlr-4 dca-emo-container">
                <div className="dca-emo-enrolled-msg asc-mt-4">
                    <div className="asc-font-bold text--center">
                        <h3>Your managed account enrollment is pending</h3>
                    </div>
                    <p className="asc-mlr-3 asc-mt-4 asc-mb-6 asc-text--center">
                        Check back in an hour to update your account.
                    </p>
                </div>
                <div className="asc-btn-group asc-btn-group--center">
                    <Button clickHandler={closeModal} text="Close" />
                </div>
            </div>
        );
    }

    return (
        <div className="asc-mb-4 asc-mlr-4 dca-emo-container">
            {dataError ? (
                <Error
                    message="This page is currently unavailable. Please check back later."
                    title="An error occurred."
                    className="dca-emo-error"
                />
            ) : (
                <>
                    {isPPTManagedAccountEnrolled ? (
                        <div className="dca-emo-enrolled-msg asc-mt-4">
                            <div className="asc-font-bold text--center">
                                <h3>You're currently enrolled in a managed account</h3>
                            </div>
                            <p className="asc-mlr-6 asc-mt-4 asc-mb-6">
                                This option automatically updates your investment strategy for you.
                                If you want to modify the funds you're invested in, you must first
                                visit the <b>Managed Accounts</b> page and opt out of this offering.
                            </p>
                        </div>
                    ) : (
                        <>
                            {getBanner()}
                            <legend className="asc-acc-hidden">
                                How would you like to invest?
                            </legend>
                            <RadioGroup
                                id="radio-group-id"
                                name="radio-group"
                                hasError={isError}
                                errorMessage={errorText}
                                optionalInputLabel=""
                                orientation="vertical"
                                onChange={handleRadioChange}
                                options={radioOptions}
                            />
                            {manageAdviceContent}
                        </>
                    )}
                    <div className="asc-btn-group asc-btn-group--center">
                        <Button
                            text="Continue"
                            clickHandler={submitForm}
                            disabled={requesting}
                        />
                    </div>
                    {samlResponse && arkPostUrl && (
                        <>
                            <form method="POST" name="ssoForm" action={arkPostUrl}>
                                <input
                                    type="hidden"
                                    name="SAMLResponse"
                                    value={samlResponse}
                                />
                            </form>
                        </>
                    )}
                </>
            )}
        </div>
    );
}

ExploreMyOptionsModal.propTypes = propTypes;

export default ExploreMyOptionsModal;
